import { Camera } from '@react-three/fiber';
import TWEEN, { Group } from '@tweenjs/tween.js'; // Ensure TWEEN is properly imported
import BezierEasing from 'bezier-easing';
import { guid4, invokeEvent } from 'shared-base';

let camera: Camera;

export const group = new Group();
const runningTweens: any = {};

let currentPosition: IValues;

export function animateCamera(
  to: any,
  duration: number = 1000,
  bezier: number[] = [0, 0.5, 0.5, 1]
) {
  try {
    const id = guid4();
    const easing = BezierEasing(bezier[0], bezier[1], bezier[2], bezier[3]);

    if (runningTweens[id]) {
      console.log('tween already running', id);
      return;
    }

    const { x, y, z } = sphericalToCartesian(to.radius, to.alpha, to.beta);

    if (!camera) {
      throw new Error('No camera');
    }

    const tween = new TWEEN.Tween(camera.position)
      .to(
        {
          x,
          y,
          z,
        },
        Math.round(duration * 1000)
      )
      .easing((t: any) => easing(t))
      .start()
      .onComplete(() => {
        console.log('Tween completed', id);
        delete runningTweens[id]; // Correct the typo
        group.remove(tween);
      });

    runningTweens[id] = tween;
    group.add(tween);
  } catch (err) {
    console.log('err =>', err);
    invokeEvent('sentry', { err });
  }
}

export const setCamera = (value: Camera) => {
  camera = value;

  currentPosition = {
    radius: 0,
    alpha: 0,
    beta: 0,
  };

  camera.fov = 30; // Reduce from default (e.g., 75) for a flatter look
  camera.updateProjectionMatrix();

  setTimeout(() => {
    animateCamera({ radius: 10, alpha: 10, beta: 30 });
  }, 4000);
};

export function stopAll() {
  console.log('Stopping all tweens:', runningTweens);
  for (const key in runningTweens) {
    const tween = runningTweens[key];
    if (tween) {
      console.log(`Stopping tween for key: ${key}`);
      group.remove(tween);
      runningTweens[key].stop();
      delete runningTweens[key];
    }
  }
}

export const sphericalToCartesian = (radius: number, alpha: number, beta: number) => {
  const alphaRad = ((90 - alpha) * Math.PI) / 180; // Adjust alpha to match the spherical convention
  const betaRad = ((beta - 90) * Math.PI) / 180; // Adjust beta to match the spherical convention

  const x = Math.round(radius * Math.sin(betaRad) * Math.cos(alphaRad));
  const y = Math.round(radius * Math.cos(betaRad));
  const z = Math.round(radius * Math.sin(betaRad) * Math.sin(alphaRad));

  return { x, y, z };
};

export const cartesianToSpherical = (x: number, y: number, z: number) => {
  const radius = Math.round(Math.sqrt(x * x + y * y + z * z));
  const betaRad = Math.atan2(z, x); // Azimuthal angle in radians
  let alphaRad = Math.acos(y / radius); // Polar angle in radians

  if (isNaN(alphaRad)) {
    alphaRad = 0;
  }

  const alpha = 90 - Math.round(alphaRad * (180 / Math.PI));
  const beta = 90 + Math.round(betaRad * (180 / Math.PI));

  return { radius, alpha, beta };
};
